import { Link } from "react-router-dom";

import { useResolvedPageMeta } from "@/hooks/useResolvedPageMeta";
import { cn } from "@/lib/utils";

/**
 * Renders the breadcrumb trail from the resolved page meta. Ancestor crumbs
 * with `href` render as links (subdued color); the final crumb renders as
 * plain text (the current page). Empty trails render nothing.
 */
export function Breadcrumbs({ className }: { className?: string }) {
  const { breadcrumbs = [] } = useResolvedPageMeta();
  if (breadcrumbs.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        "flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground",
        className,
      )}
    >
      {breadcrumbs.map((crumb, i) => {
        const isLast = i === breadcrumbs.length - 1;
        const separator = i > 0 && (
          <span aria-hidden="true" className="text-text-dim">
            ›
          </span>
        );

        const content =
          crumb.href && !isLast ? (
            <Link
              to={crumb.href}
              className="truncate text-muted-foreground hover:text-foreground"
            >
              {crumb.label}
            </Link>
          ) : (
            <span className={cn("truncate", isLast && "text-foreground")}>
              {crumb.label}
            </span>
          );

        return (
          <div key={`${i}-${crumb.label}`} className="flex min-w-0 items-center gap-1.5">
            {separator}
            {content}
          </div>
        );
      })}
    </nav>
  );
}
